import { RETENTION_DAYS, SESSION_LEN_BINS_SEC } from "./config";

// 端末ローカル日付をキーに集計。Date#toISOString は UTC で出るので使わない。

export interface DayMetricAcc {
  count: number;
  sumMs: number;
  maxMs: number;
}

/** 1日分の利用統計 (集計値のみ。生座標・生タイムスタンプ列は構造的に持たない) */
export interface DayRecord {
  date: string; // YYYY-MM-DD
  taps: number; // 総タップ数
  sessions: number; // 操作バースト数
  errors: number;
  hourly: number[]; // 時間帯別タップ数 [24]
  byScreen: Record<string, number>; // 画面(data-scr)別タップ数
  searches: Record<string, number>; // facilityId -> 経路探索回数
  route: DayMetricAcc; // 経路探索の計算時間
  render: DayMetricAcc; // フロアデータ取得+地図描画 (初回ロード含む)
  sessLen: number[]; // セッション長ヒストグラム [SESSION_LEN_BINS_SEC.length + 1]
}

export const dateKeyOf = (ms: number): string => {
  const d = new Date(ms);
  const pad = (n: number): string => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const emptyMetric = (): DayMetricAcc => ({ count: 0, sumMs: 0, maxMs: 0 });

export const emptyDay = (date: string): DayRecord => ({
  date,
  taps: 0,
  sessions: 0,
  errors: 0,
  hourly: new Array(24).fill(0) as number[],
  byScreen: {},
  searches: {},
  route: emptyMetric(),
  render: emptyMetric(),
  sessLen: new Array(SESSION_LEN_BINS_SEC.length + 1).fill(0) as number[],
});

export const sessionLenBin = (durationMs: number): number => {
  const sec = durationMs / 1000;
  for (let i = 0; i < SESSION_LEN_BINS_SEC.length; i += 1) {
    if (sec <= SESSION_LEN_BINS_SEC[i]) return i;
  }
  return SESSION_LEN_BINS_SEC.length;
};

export class DayStore {
  private buckets = new Map<string, DayRecord>();

  /** 指定時刻の日付バケットを取得 (無ければ作成し古い日を退避) */
  ensure(atMs: number): DayRecord {
    const key = dateKeyOf(atMs);
    let bucket = this.buckets.get(key);
    if (!bucket) {
      bucket = emptyDay(key);
      this.buckets.set(key, bucket);
      this.evictOld();
    }
    return bucket;
  }

  tap(screen: string, atMs: number): void {
    const b = this.ensure(atMs);
    b.taps += 1;
    b.hourly[new Date(atMs).getHours()] += 1;
    b.byScreen[screen] = (b.byScreen[screen] ?? 0) + 1;
  }

  session(atMs: number): void {
    this.ensure(atMs).sessions += 1;
  }

  /** 確定したセッション長を、セッション開始日のバケットに積む */
  sessionLength(durationMs: number, startedAtMs: number): void {
    if (!Number.isFinite(durationMs) || durationMs < 0) return;
    this.ensure(startedAtMs).sessLen[sessionLenBin(durationMs)] += 1;
  }

  error(atMs: number): void {
    this.ensure(atMs).errors += 1;
  }

  // 座標目的地 (facilityId なし) は集計対象外。
  search(facilityId: string | null | undefined, atMs: number): void {
    if (!facilityId) return;
    const b = this.ensure(atMs);
    b.searches[facilityId] = (b.searches[facilityId] ?? 0) + 1;
  }

  metric(name: "route" | "render", valueMs: number, atMs: number): void {
    if (!Number.isFinite(valueMs) || valueMs < 0) return;
    const m = this.ensure(atMs)[name];
    m.count += 1;
    m.sumMs += valueMs;
    if (valueMs > m.maxMs) m.maxMs = valueMs;
  }

  /** 古い→新しい順 */
  list(): DayRecord[] {
    return Array.from(this.buckets.values()).sort((a, b) => a.date.localeCompare(b.date));
  }

  serialize(): Record<string, DayRecord> {
    return Object.fromEntries(this.buckets);
  }

  // 型が想定外のバケットは捨てて続行 (本体UIを落とさない方針)。
  load(data: unknown): void {
    this.buckets.clear();
    if (!data || typeof data !== "object") return;
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      const parsed = parseDay(value);
      if (parsed) this.buckets.set(key, parsed);
    }
    this.evictOld();
  }

  reset(): void {
    this.buckets.clear();
  }

  // YYYY-MM-DD の辞書順 = 時系列順を利用して古い方から落とす。
  private evictOld(): void {
    if (this.buckets.size <= RETENTION_DAYS) return;
    const sortedKeys = Array.from(this.buckets.keys()).sort();
    const removeCount = sortedKeys.length - RETENTION_DAYS;
    for (let i = 0; i < removeCount; i += 1) {
      this.buckets.delete(sortedKeys[i]);
    }
  }
}

const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);

const numArray = (v: unknown, len: number): number[] => {
  const out = new Array(len).fill(0) as number[];
  if (Array.isArray(v)) {
    for (let i = 0; i < len; i += 1) out[i] = num(v[i]);
  }
  return out;
};

const numRecord = (v: unknown): Record<string, number> => {
  if (!v || typeof v !== "object") return {};
  const out: Record<string, number> = {};
  for (const [k, n] of Object.entries(v as Record<string, unknown>)) {
    if (typeof n === "number" && Number.isFinite(n)) out[k] = n;
  }
  return out;
};

const parseMetric = (v: unknown): DayMetricAcc => {
  if (!v || typeof v !== "object") return emptyMetric();
  const m = v as Partial<DayMetricAcc>;
  return { count: num(m.count), sumMs: num(m.sumMs), maxMs: num(m.maxMs) };
};

const parseDay = (value: unknown): DayRecord | null => {
  if (!value || typeof value !== "object") return null;
  const v = value as Partial<DayRecord>;
  if (typeof v.date !== "string") return null;
  return {
    date: v.date,
    taps: num(v.taps),
    sessions: num(v.sessions),
    errors: num(v.errors),
    hourly: numArray(v.hourly, 24),
    byScreen: numRecord(v.byScreen),
    searches: numRecord(v.searches),
    route: parseMetric(v.route),
    render: parseMetric(v.render),
    sessLen: numArray(v.sessLen, SESSION_LEN_BINS_SEC.length + 1),
  };
};
